import React, { useState } from 'react';
import { Save, Shield, AlertCircle, CheckCircle2, Calendar, Info } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useApp } from '../context/AppContext';
import { PUBLIC_HOLIDAYS_2025 } from '../data/mockData';

function SettingRow({ label, description, children }) {
  return (
    <div className="flex items-center justify-between gap-6 py-3.5 border-b border-sand-100 last:border-0">
      <div className="flex-1">
        <p className="text-sm font-medium text-sand-900">{label}</p>
        {description && <p className="text-xs text-sand-400 mt-0.5">{description}</p>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className="relative flex-shrink-0 transition-colors duration-200 rounded-full"
      style={{ width: 40, height: 22, background: value ? 'rgb(1,43,99)' : '#d6d2c6' }}
    >
      <span
        className="absolute transition-all duration-200 rounded-full bg-white"
        style={{
          width: 16, height: 16,
          top: 3, left: value ? 21 : 3,
          boxShadow: '0 1px 4px rgba(0,0,0,0.18)'
        }}
      />
    </button>
  );
}

function SectionHeader({ icon: Icon, title, description, badge, badgeColor }) {
  return (
    <div className="flex items-center justify-between mb-1 pb-4 border-b border-sand-100">
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: '#EEF3FB' }}
        >
          <Icon size={16} style={{ color: 'rgb(1,43,99)' }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-sand-900">{title}</h3>
          {description && <p className="text-xs text-sand-400 mt-0.5">{description}</p>}
        </div>
      </div>
      {badge && (
        <span
          className="text-xs font-semibold px-2.5 py-1 rounded-full"
          style={{ background: badgeColor?.bg || '#EEF3FB', color: badgeColor?.text || 'rgb(1,43,99)' }}
        >
          {badge}
        </span>
      )}
    </div>
  );
}

function UnitPill({ label, color = 'blue' }) {
  const colors = {
    blue:  { bg: '#EEF3FB', text: '#1e3a6e' },
    amber: { bg: '#fef9c3', text: '#92400e' },
    green: { bg: '#dcfce7', text: '#166534' },
  };
  const c = colors[color];
  return (
    <span
      className="text-xs font-semibold px-2 py-0.5 rounded-full ml-2"
      style={{ background: c.bg, color: c.text }}
    >
      {label}
    </span>
  );
}

export default function Policy() {
  const { policy, updatePolicy } = useApp();
  const [local, setLocal] = useState({
    annualDays: 28,
    annualHours: 210,
    ptDays: 11.2,
    ptHours: 84,
    bankHols: 8,
    sickDays: 0,
    carryOver: 5,
    yearStart: 'January',
    bookingUnit: 'Days',
    noticeWeeks: 2,
    maxConsecutive: 20,
    approvalRequired: true,
    allowHalfDays: true,
    allowHours: true,
    allowToil: false,
    ...policy,
  });
  const [saved, setSaved] = useState(false);

  const set = (k, v) => { setSaved(false); setLocal(p => ({ ...p, [k]: v })); };
  const setNum = (k, v) => set(k, Number(v));

  const handleSave = () => {
    updatePolicy(local);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const hasChanges = JSON.stringify(local) !== JSON.stringify({
    annualDays: 28, annualHours: 210, ptDays: 11.2, ptHours: 84,
    bankHols: 8, sickDays: 0, carryOver: 5, yearStart: 'January',
    bookingUnit: 'Days', noticeWeeks: 2, maxConsecutive: 20,
    approvalRequired: true, allowHalfDays: true, allowHours: true, allowToil: false,
    ...policy, ...local,
  });

  const inputStyle = {
    width: 80, height: 36, textAlign: 'center',
    border: '0.5px solid #d6d2c6', borderRadius: 8,
    fontSize: 14, fontWeight: 500, color: '#1a1917',
    background: '#fff', outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };

  const readonlyStyle = {
    ...inputStyle,
    background: '#faf9f6', color: '#8c8779', borderStyle: 'dashed',
  };

  const selectStyle = {
    height: 36, minWidth: 130, padding: '0 10px',
    border: '0.5px solid #d6d2c6', borderRadius: 8,
    fontSize: 13, color: '#1a1917', background: '#fff', outline: 'none',
  };

  const cardStyle = {
    borderRadius: 12,
    border: '0.5px solid #e5eaf2',
    background: '#ffffff',        /* ← explicit white, overrides .card's bg-white in case */
    overflow: 'hidden',
  };

  return (
    // ↓ policy-page class forces white bg even if parent is sand-coloured
    <div className="policy-page flex flex-col gap-4 animate-fade-in" style={{ maxWidth: 1200 }}>

      {/* Page header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 600, color: '#0a1628', letterSpacing: '-0.02em' }}>
            Policy Settings
          </h2>
          <p className="text-xs text-sand-400 mt-1">UK statutory leave configuration for all employees</p>
        </div>
        <span
          className="text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5"
          style={{ background: 'rgb(1,43,99)', color: '#fff', marginTop: 2 }}
        >
          <Shield size={11} /> Working Time Regs 1998
        </span>
      </div>

      {/* Save bar */}
      <div
        className="flex items-center justify-between px-4 py-3 rounded-xl"
        style={{ background: '#f8faff', border: '0.5px solid #d0e2f7' }}
      >
        <div className="flex items-center gap-2">
          {saved ? (
            <>
              <CheckCircle2 size={14} style={{ color: '#16a34a' }} />
              <span style={{ fontSize: 13, fontWeight: 500, color: '#16a34a' }}>Policy saved successfully</span>
            </>
          ) : hasChanges ? (
            <>
              <AlertCircle size={13} style={{ color: '#b45309' }} />
              <span style={{ fontSize: 13, fontWeight: 500, color: '#b45309' }}>Unsaved changes</span>
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#8c8779' }}>No unsaved changes</span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges && !saved}
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '8px 20px',
            background: saved ? '#16a34a' : 'rgb(1,43,99)',
            color: '#fff', border: 'none', borderRadius: 8,
            fontSize: 13, fontWeight: 500,
            cursor: hasChanges ? 'pointer' : 'default',
            opacity: (!hasChanges && !saved) ? 0.4 : 1,
            transition: 'all 0.15s',
          }}
        >
          {saved
            ? <><CheckCircle2 size={14} /> Saved!</>
            : <><Save size={14} /> Save Policy</>}
        </button>
      </div>

      {/* ── Leave Entitlement ── */}
      <div style={cardStyle}>
        <div style={{ padding: '18px 20px 0' }}>
          <SectionHeader
            icon={Shield}
            title="Leave Entitlement"
            description="UK min: 28 days (5.6 weeks) inc. 8 bank holidays"
            badge="Statutory"
            badgeColor={{ bg: '#EEF3FB', text: 'rgb(1,43,99)' }}
          />
          <div
            className="flex items-start gap-2 text-xs rounded-lg px-3 py-2.5 my-3"
            style={{ background: '#EEF3FB', color: '#0c3460', borderLeft: '3px solid rgb(1,43,99)' }}
          >
            <Info size={13} style={{ marginTop: 1, flexShrink: 0, color: 'rgb(1,43,99)' }} />
            <span>
              <strong>Full Time 37.5 hrs/week:</strong> 28 days = 210 hrs &nbsp;·&nbsp;
              <strong>Part Time 15 hrs/week:</strong> 11.2 days = 84 hrs (0.40 FTE)
            </span>
          </div>
        </div>
        <div style={{ padding: '0 20px 4px' }}>
          <SettingRow label="Full-Time Annual Leave Days" description="Statutory minimum 28 days (inc. 8 bank holidays)">
            <div className="flex items-center">
              <input style={inputStyle} type="number" min={28} max={50} value={local.annualDays}
                onChange={e => { setNum('annualDays', e.target.value); set('annualHours', Math.round(e.target.value * 7.5 * 10) / 10); }} />
              <UnitPill label="days" color="blue" />
            </div>
          </SettingRow>
          <SettingRow label="Full-Time Annual Leave (Hours)" description="Auto-calculated: days × 7.5 hrs">
            <div className="flex items-center">
              <input style={readonlyStyle} type="number" value={local.annualHours} readOnly />
              <UnitPill label="hrs" color="blue" />
            </div>
          </SettingRow>
          <SettingRow label="Part-Time Annual Leave Days" description="Pro-rata: 15 ÷ 37.5 × 28 = 11.2 days">
            <div className="flex items-center">
              <input style={inputStyle} type="number" step="0.1" value={local.ptDays}
                onChange={e => { set('ptDays', e.target.value); set('ptHours', Math.round(e.target.value * 7.5 * 10) / 10); }} />
              <UnitPill label="days" color="blue" />
            </div>
          </SettingRow>
          <SettingRow label="Part-Time Annual Leave (Hours)" description="Auto-calculated: days × 7.5 hrs">
            <div className="flex items-center">
              <input style={readonlyStyle} type="number" value={local.ptHours} readOnly />
              <UnitPill label="hrs" color="blue" />
            </div>
          </SettingRow>
          <SettingRow label="Bank Holidays Included" description="Counted within annual total (UK: 8)">
            <div className="flex items-center">
              <input style={inputStyle} type="number" min={0} max={8} value={local.bankHols}
                onChange={e => setNum('bankHols', e.target.value)} />
              <UnitPill label="days" color="blue" />
            </div>
          </SettingRow>
          <SettingRow label="Sick Leave Days" description="Paid sick leave per year above SSP">
            <div className="flex items-center">
              <input style={inputStyle} type="number" min={0} max={30} value={local.sickDays}
                onChange={e => setNum('sickDays', e.target.value)} />
              <UnitPill label="days" color="amber" />
            </div>
          </SettingRow>
          <SettingRow label="Carry-Over Days" description="Max days carried into the next leave year">
            <div className="flex items-center">
              <input style={inputStyle} type="number" min={0} max={20} value={local.carryOver}
                onChange={e => setNum('carryOver', e.target.value)} />
              <UnitPill label="days" color="amber" />
            </div>
          </SettingRow>
          <SettingRow label="Leave Year Start" description="Start month for the annual leave year">
            <select style={selectStyle} value={local.yearStart} onChange={e => set('yearStart', e.target.value)}>
              {['January', 'March', 'April', 'June'].map(m => <option key={m}>{m}</option>)}
            </select>
          </SettingRow>
          <SettingRow label="Default Booking Unit" description="How leave is measured by default">
            <select style={selectStyle} value={local.bookingUnit} onChange={e => set('bookingUnit', e.target.value)}>
              <option>Days</option>
              <option>Hours</option>
            </select>
          </SettingRow>
        </div>
      </div>

      {/* ── Request Rules ── */}
      <div style={cardStyle}>
        <div style={{ padding: '18px 20px 0' }}>
          <SectionHeader
            icon={Calendar}
            title="Request Rules"
            description="Control how employees submit and plan leave"
            badge="Active"
            badgeColor={{ bg: '#dcfce7', text: '#166534' }}
          />
        </div>
        <div style={{ padding: '0 20px 4px' }}>
          <SettingRow label="Minimum Notice (weeks)" description="How far in advance leave must be requested">
            <div className="flex items-center">
              <input style={inputStyle} type="number" min={0} max={12} value={local.noticeWeeks}
                onChange={e => setNum('noticeWeeks', e.target.value)} />
              <UnitPill label="wks" color="blue" />
            </div>
          </SettingRow>
          <SettingRow label="Max Consecutive Days" description="Longest single approved leave block">
            <div className="flex items-center">
              <input style={inputStyle} type="number" min={1} max={60} value={local.maxConsecutive}
                onChange={e => setNum('maxConsecutive', e.target.value)} />
              <UnitPill label="days" color="blue" />
            </div>
          </SettingRow>
          <SettingRow label="Approval Required" description="All requests must be approved by a manager">
            <div className="flex items-center gap-2">
              <Toggle value={local.approvalRequired} onChange={v => set('approvalRequired', v)} />
              <span style={{ fontSize: 12, color: '#6b6760', minWidth: 24 }}>
                {local.approvalRequired ? 'Yes' : 'No'}
              </span>
            </div>
          </SettingRow>
          <SettingRow label="Allow Half Days" description="Employees can book AM or PM half days">
            <div className="flex items-center gap-2">
              <Toggle value={local.allowHalfDays} onChange={v => set('allowHalfDays', v)} />
              <span style={{ fontSize: 12, color: '#6b6760', minWidth: 24 }}>
                {local.allowHalfDays ? 'Yes' : 'No'}
              </span>
            </div>
          </SettingRow>
          <SettingRow label="Allow Hourly Booking" description="Employees can book leave in hours (for PT workers)">
            <div className="flex items-center gap-2">
              <Toggle value={local.allowHours} onChange={v => set('allowHours', v)} />
              <span style={{ fontSize: 12, color: '#6b6760', minWidth: 24 }}>
                {local.allowHours ? 'Yes' : 'No'}
              </span>
            </div>
          </SettingRow>
          <SettingRow label="Allow TOIL" description="Time Off In Lieu can be accrued and booked">
            <div className="flex items-center gap-2">
              <Toggle value={local.allowToil} onChange={v => set('allowToil', v)} />
              <span style={{ fontSize: 12, color: '#6b6760', minWidth: 24 }}>
                {local.allowToil ? 'Yes' : 'No'}
              </span>
            </div>
          </SettingRow>
        </div>
      </div>

      {/* ── Public Holidays ── */}
      <div style={cardStyle}>
        <div style={{ padding: '18px 20px 14px' }}>
          <SectionHeader
            icon={Info}
            title="UK Public Holidays 2025"
            description="Excluded from leave calculations — England & Wales"
            badge="8 days"
            badgeColor={{ bg: '#dcfce7', text: '#166534' }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          {PUBLIC_HOLIDAYS_2025.map((h, i) => (
            <div
              key={h.date}
              className="flex items-center justify-between px-5 py-2.5 transition-colors hover:bg-slate-50"
              style={{
                borderBottom: i < PUBLIC_HOLIDAYS_2025.length - 2 ? '0.5px solid #f2f0ea' : 'none',
                borderRight: i % 2 === 0 ? '0.5px solid #f2f0ea' : 'none',
              }}
            >
              <p style={{ fontSize: 13, color: '#0a1628' }}>{h.name}</p>
              <span
                style={{
                  fontSize: 11, color: '#504d47', background: '#f2f0ea',
                  padding: '2px 8px', borderRadius: 20, fontVariantNumeric: 'tabular-nums',
                }}
              >
                {format(parseISO(h.date), 'EEE, MMM d')}
              </span>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}